<template>
  <div class="workflow-page" :class="{ 'workflow-page--prompt-mode': activeWorkspaceTab === 'prompts' }">
    <div class="workflow-sidebar page-card">
      <div class="workflow-sidebar__head">
        <div>
          <div class="page-card__title">Agent 流程列表</div>
          <div class="page-card__desc">维护节点编排流程，后续可继续接入真实执行链路。</div>
        </div>
        <button class="action-button" @click="createDraftWorkflow">新建</button>
      </div>

      <div v-if="loading" class="page-state">加载中...</div>

      <div class="workflow-list">
        <button
          v-for="item in workflows"
          :key="item.id"
          type="button"
          class="workflow-item"
          :class="{ 'workflow-item--active': draft.id === item.id }"
          @click="openWorkflow(item.id)"
        >
          <div class="workflow-item__title">{{ item.name }}</div>
          <div class="workflow-item__meta">
            <span>{{ workflowStatusText(item.status) }}</span>
            <span>v{{ item.version || 1 }}</span>
          </div>
          <div class="workflow-item__desc">{{ item.description || item.code }}</div>
        </button>
      </div>

      <div class="sidebar-section">
        <div class="sidebar-section__title">节点目录</div>
        <div class="catalog-list">
          <button
            v-for="item in nodeCatalog"
            :key="item.type"
            type="button"
            class="catalog-item"
            @click="addNode(item)"
          >
            <span class="catalog-item__label">{{ item.label }}</span>
            <span class="catalog-item__type">{{ catalogCategoryText(item.category) }}</span>
          </button>
        </div>

      </div>
    </div>

    <div class="workflow-main">
      <div class="page-card workflow-toolbar">
        <div class="toolbar-fields">
          <label class="form-field">
            <span>流程名称</span>
            <input v-model.trim="draft.name" type="text" placeholder="例如：宠物记录 Agent 流程" />
          </label>
          <label class="form-field">
            <span>流程编码</span>
            <input v-model.trim="draft.code" type="text" placeholder="例如：pet-care-agent" />
          </label>
          <label class="form-field form-field--small">
            <span>状态</span>
            <select v-model="draft.status">
              <option value="DRAFT">草稿</option>
              <option value="ACTIVE">启用</option>
              <option value="DISABLED">停用</option>
            </select>
          </label>
        </div>

        <div class="toolbar-actions">
          <button class="ghost-button" @click="createDraftWorkflow">重置</button>
          <button class="ghost-button" :disabled="!draft.id" @click="removeCurrentWorkflow">删除</button>
          <button class="action-button" :disabled="saving" @click="saveWorkflow">
            {{ saving ? "保存中..." : "保存流程" }}
          </button>
        </div>
      </div>

      <div class="page-card flow-description-card">
        <label class="form-field">
          <span>流程说明</span>
          <textarea
            v-model.trim="draft.description"
            rows="2"
            placeholder="描述这个流程适用于什么业务场景。"
          />
        </label>
      </div>

      <div class="page-card workspace-switcher">
        <div class="workspace-switcher__tabs">
          <button
            type="button"
            class="workspace-switcher__tab"
            :class="{ 'workspace-switcher__tab--active': activeWorkspaceTab === 'prompts' }"
            @click="activeWorkspaceTab = 'prompts'"
          >
            {{ "提示词管理" }}
          </button>
          <button
            type="button"
            class="workspace-switcher__tab"
            :class="{ 'workspace-switcher__tab--active': activeWorkspaceTab === 'canvas' }"
            @click="activeWorkspaceTab = 'canvas'"
          >
            {{ "流程图" }}
          </button>
        </div>
        <div class="workspace-switcher__hint">
          {{ activeWorkspaceTab === "prompts" ? "当前只显示提示词编辑区域。" : "当前只显示流程图和节点编排区域。" }}
        </div>
      </div>

      <div v-if="activeWorkspaceTab === 'prompts'" class="page-card prompt-workbench">
        <div class="page-card__title">{{ "流程提示词管理" }}</div>
        <div class="page-card__desc">{{ "按模块切换编辑，避免流程图和长提示词同时占满界面。" }}</div>

        <div class="prompt-switcher">
          <button
            type="button"
            class="prompt-switcher__item"
            :class="{ 'prompt-switcher__item--active': activePromptPanel === 'main-system' }"
            @click="activePromptPanel = 'main-system'"
          >
            <strong>{{ "主模型 System" }}</strong>
            <span>invoke-model.systemPrompt</span>
          </button>
          <button
            type="button"
            class="prompt-switcher__item"
            :class="{ 'prompt-switcher__item--active': activePromptPanel === 'main-user' }"
            @click="activePromptPanel = 'main-user'"
          >
            <strong>{{ "主模型 User" }}</strong>
            <span>build-prompt.userPromptTemplate</span>
          </button>
          <button
            type="button"
            class="prompt-switcher__item"
            :class="{ 'prompt-switcher__item--active': activePromptPanel === 'reply' }"
            @click="activePromptPanel = 'reply'"
          >
            <strong>{{ "结果回复" }}</strong>
            <span>reply-render.*</span>
          </button>
        </div>

        <div class="prompt-workbench__layout">
          <div class="prompt-panel">
            <div v-if="activePromptPanel === 'main-system'" class="prompt-editor prompt-editor--flat">
              <div class="prompt-panel__head">
                <div>
                  <div class="prompt-editor__title">{{ "主模型 System Prompt" }}</div>
                  <div class="prompt-panel__meta">invoke-model.systemPrompt</div>
                </div>
                <button class="ghost-button ghost-button--mini" type="button" @click="restoreWorkflowNodePromptField('invoke-model', 'systemPrompt')">
                  {{ "恢复默认" }}
                </button>
              </div>
              <div class="prompt-field">
                <div class="prompt-field__head">
                  <span class="prompt-field__label">{{ "内容" }}</span>
                  <span class="prompt-field__path">invoke-model.systemPrompt</span>
                </div>
                <textarea
                  class="prompt-field__textarea"
                  :value="workflowPromptFieldValue('invoke-model', 'systemPrompt')"
                  rows="16"
                  spellcheck="false"
                  @input="updateWorkflowNodePromptField('invoke-model', 'systemPrompt', $event.target.value)"
                />
              </div>
              <div class="prompt-editor__vars">
                <button
                  v-for="item in workflowPromptTokenDetails('build-prompt')"
                  :key="'ms-' + item.token"
                  type="button"
                  class="token-button"
                  @click="appendWorkflowNodePromptToken('invoke-model', 'systemPrompt', item.token)"
                >
                  {{ item.token }}
                </button>
              </div>
            </div>

            <div v-else-if="activePromptPanel === 'main-user'" class="prompt-editor prompt-editor--flat">
              <div class="prompt-panel__head">
                <div>
                  <div class="prompt-editor__title">{{ "主模型 User Prompt" }}</div>
                  <div class="prompt-panel__meta">build-prompt.userPromptTemplate</div>
                </div>
                <button class="ghost-button ghost-button--mini" type="button" @click="restoreWorkflowNodePromptField('build-prompt', 'userPromptTemplate')">
                  {{ "恢复默认" }}
                </button>
              </div>
              <div class="prompt-field">
                <div class="prompt-field__head">
                  <span class="prompt-field__label">{{ "内容" }}</span>
                  <span class="prompt-field__path">build-prompt.userPromptTemplate</span>
                </div>
                <textarea
                  class="prompt-field__textarea"
                  :value="workflowPromptFieldValue('build-prompt', 'userPromptTemplate')"
                  rows="16"
                  spellcheck="false"
                  @input="updateWorkflowNodePromptField('build-prompt', 'userPromptTemplate', $event.target.value)"
                />
              </div>
              <div class="prompt-editor__vars">
                <button
                  v-for="item in workflowPromptTokenDetails('build-prompt')"
                  :key="'mu-' + item.token"
                  type="button"
                  class="token-button"
                  @click="appendWorkflowNodePromptToken('build-prompt', 'userPromptTemplate', item.token)"
                >
                  {{ item.token }}
                </button>
              </div>
            </div>

            <div v-else class="prompt-editor prompt-editor--flat">
              <div class="prompt-panel__head">
                <div>
                  <div class="prompt-editor__title">{{ "结果回复 Prompt" }}</div>
                  <div class="prompt-panel__meta">reply-render.systemPrompt / userPromptTemplate / maxTokens</div>
                </div>
                <button class="ghost-button ghost-button--mini" type="button" @click="restoreWorkflowPromptGroup('reply-render')">
                  {{ "恢复默认" }}
                </button>
              </div>
              <div class="prompt-field">
                <div class="prompt-field__head">
                  <span class="prompt-field__label">System Prompt</span>
                  <span class="prompt-field__path">reply-render.systemPrompt</span>
                </div>
                <textarea
                  class="prompt-field__textarea prompt-field__textarea--compact"
                  :value="workflowPromptFieldValue('reply-render', 'systemPrompt')"
                  rows="8"
                  spellcheck="false"
                  @input="updateWorkflowNodePromptField('reply-render', 'systemPrompt', $event.target.value)"
                />
              </div>
              <div class="prompt-field">
                <div class="prompt-field__head">
                  <span class="prompt-field__label">User Prompt Template</span>
                  <span class="prompt-field__path">reply-render.userPromptTemplate</span>
                </div>
                <textarea
                  class="prompt-field__textarea"
                  :value="workflowPromptFieldValue('reply-render', 'userPromptTemplate')"
                  rows="10"
                  spellcheck="false"
                  @input="updateWorkflowNodePromptField('reply-render', 'userPromptTemplate', $event.target.value)"
                />
              </div>
              <div class="prompt-editor__vars">
                <button
                  v-for="item in workflowPromptTokenDetails('reply-render')"
                  :key="'rp-' + item.token"
                  type="button"
                  class="token-button"
                  @click="appendWorkflowNodePromptToken('reply-render', 'userPromptTemplate', item.token)"
                >
                  {{ item.token }}
                </button>
              </div>
              <label class="prompt-inline-field">
                <span>Max Tokens</span>
                <input
                  :value="workflowPromptFieldValue('reply-render', 'maxTokens')"
                  type="number"
                  min="1"
                  @input="updateWorkflowNodePromptNumberField('reply-render', 'maxTokens', $event.target.value)"
                />
              </label>
            </div>
          </div>

          <div class="prompt-sidecard prompt-sidecard--sticky">
            <div class="prompt-sidecard__title">{{ "变量说明" }}</div>
            <div class="prompt-sidecard__desc">
              {{ activePromptPanel === "reply" ? "这些变量用于结果回复节点。" : "这些变量用于主模型上下文与决策。" }}
            </div>
            <div class="prompt-token-list">
              <div
                v-for="item in activePromptPanel === 'reply' ? workflowPromptTokenDetails('reply-render') : workflowPromptTokenDetails('build-prompt')"
                :key="'panel-token-' + item.token"
                class="prompt-token-item"
              >
                <code>{{ item.token }}</code>
                <span>{{ item.description }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="activeWorkspaceTab === 'canvas'" class="page-card canvas-card">
        <div class="canvas-head">
          <div>
            <div class="page-card__title">流程画布</div>
            <div class="page-card__desc">
              节点头部可拖拽，点击“从这里连线”后，再到目标节点点击“连到这里”。
            </div>
          </div>
          <div class="canvas-meta">
            <span>{{ draft.graph.nodes.length }} 个节点</span>
            <span>{{ draft.graph.edges.length }} 条连线</span>
          </div>
        </div>

        <div v-if="linkSourceNodeId" class="link-banner">
          <span>正在连线：起点为“{{ linkSourceNodeLabel }}”，请选择目标节点。</span>
          <button class="mini-button" type="button" @click="cancelLinkMode">取消</button>
        </div>

        <div
          ref="canvasShell"
          class="canvas-shell"
          :class="{ 'canvas-shell--panning': Boolean(canvasPanState) }"
          @click="cancelLinkModeIfBlank"
        >
          <div class="canvas-area" @mousedown="startCanvasPan">
            <svg class="canvas-svg">
              <defs>
                <marker
                  id="workflow-arrow"
                  markerWidth="10"
                  markerHeight="10"
                  refX="8"
                  refY="3"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M0,0 L0,6 L9,3 z" fill="#7c8aa5" />
                </marker>
              </defs>
              <g v-for="edge in edgeLines" :key="edge.id">
                <line
                  class="edge-line"
                  :class="{ 'edge-line--active': selectedEdgeId === edge.id }"
                  :x1="edge.x1"
                  :y1="edge.y1"
                  :x2="edge.x2"
                  :y2="edge.y2"
                  marker-end="url(#workflow-arrow)"
                  @click.stop="selectEdge(edge.id)"
                />
                <text
                  v-if="edge.label"
                  class="edge-label"
                  :x="edge.labelX"
                  :y="edge.labelY"
                  @click.stop="selectEdge(edge.id)"
                >
                  {{ edge.label }}
                </text>
              </g>
            </svg>

            <div
              v-for="node in draft.graph.nodes"
              :key="node.id"
              class="flow-node"
              :class="{
                'flow-node--selected': selectedNodeId === node.id,
                'flow-node--linking': linkSourceNodeId === node.id,
                'flow-node--link-target': canLinkToNode(node.id)
              }"
              :style="{ left: `${node.x}px`, top: `${node.y}px` }"
              @click.stop="selectNode(node.id)"
            >
              <div class="flow-node__top">
                <span class="flow-node__type">{{ node.type }}</span>
                <button type="button" class="node-icon-button" @click.stop="removeNode(node.id)">×</button>
              </div>

              <div class="flow-node__drag-handle" @mousedown="startNodeDrag(node, $event)">
                <span>拖拽节点</span>
              </div>

              <div class="flow-node__title">{{ node.label }}</div>
              <div class="flow-node__desc">{{ node.description || "暂无说明" }}</div>

              <div class="flow-node__actions">
                <button
                  v-if="linkSourceNodeId !== node.id"
                  type="button"
                  class="mini-button"
                  @click.stop="toggleLinkMode(node.id)"
                >
                  从这里连线
                </button>
                <button
                  v-else
                  type="button"
                  class="mini-button mini-button--warn"
                  @click.stop="cancelLinkMode"
                >
                  取消连线
                </button>
                <button
                  v-if="canLinkToNode(node.id)"
                  type="button"
                  class="mini-button mini-button--primary"
                  @click.stop="connectToNode(node.id)"
                >
                  连到这里
                </button>
                <button type="button" class="mini-button" @click.stop="duplicateNode(node)">复制</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="activeWorkspaceTab === 'canvas'" class="workflow-inspector page-card">
      <div class="page-card__title">属性面板</div>
      <div class="page-card__desc">查看并编辑当前选中的节点或连线。</div>

      <div v-if="selectedNode" class="inspector-section">
        <div class="inspector-section__title">当前节点</div>
        <label class="form-field">
          <span>节点名称</span>
          <input v-model.trim="selectedNode.label" type="text" />
        </label>
        <label class="form-field">
          <span>节点说明</span>
          <textarea v-model.trim="selectedNode.description" rows="3" />
        </label>
        <div class="position-row">
          <label class="form-field form-field--small">
            <span>X 坐标</span>
            <input v-model.number="selectedNode.x" type="number" />
          </label>
          <label class="form-field form-field--small">
            <span>Y 坐标</span>
            <input v-model.number="selectedNode.y" type="number" />
          </label>
        </div>
        <div v-if="isModelNode(selectedNode)" class="prompt-editor">
          <div class="prompt-editor__title">提示词配置</div>
          <label class="form-field">
            <span>System Prompt</span>
            <textarea
              :value="selectedNode.config.systemPrompt || ''"
              rows="10"
              spellcheck="false"
              @input="updateSelectedNodeConfigField('systemPrompt', $event.target.value)"
            />
          </label>
          <div class="prompt-editor__vars">
            <button
              v-for="token in modelPromptVariableTokens(selectedNode)"
              :key="`system-${token}`"
              type="button"
              class="token-button"
              @click="appendPromptToken('systemPrompt', token)"
            >
              {{ token }}
            </button>
          </div>
          <label class="form-field">
            <span>User Prompt Template</span>
            <textarea
              :value="selectedNode.config.userPromptTemplate || ''"
              rows="10"
              spellcheck="false"
              @input="updateSelectedNodeConfigField('userPromptTemplate', $event.target.value)"
            />
          </label>
          <div class="prompt-editor__vars">
            <button
              v-for="token in modelPromptVariableTokens(selectedNode)"
              :key="`user-${token}`"
              type="button"
              class="token-button"
              @click="appendPromptToken('userPromptTemplate', token)"
            >
              {{ token }}
            </button>
          </div>
          <label class="form-field form-field--small">
            <span>Max Tokens</span>
            <input
              :value="selectedNode.config.maxTokens || ''"
              type="number"
              min="1"
              @input="updateSelectedNodeConfigNumberField('maxTokens', $event.target.value)"
            />
          </label>
          <div class="prompt-editor__hint">
            可用变量：`{{message}}`、`{{pets}}`、`{{memory}}`、`{{recentHistory}}`、`{{draft}}`、`{{lastAiMessage}}`、`{{lastUserMessage}}`
          </div>
        </div>
        <label class="form-field">
          <span>节点配置 JSON</span>
          <textarea v-model="nodeConfigDraft" rows="12" spellcheck="false" @blur="applyNodeConfig" />
        </label>
      </div>

      <div v-else-if="selectedEdge" class="inspector-section">
        <div class="inspector-section__title">当前连线</div>
        <label class="form-field">
          <span>连线标签</span>
          <input v-model.trim="selectedEdge.label" type="text" placeholder="例如：成功 / 失败 / 需要补充字段" />
        </label>
        <div class="edge-summary">
          <div>起点：{{ edgeNodeLabel(selectedEdge.source) }}</div>
          <div>终点：{{ edgeNodeLabel(selectedEdge.target) }}</div>
        </div>
        <button class="ghost-button ghost-button--danger" @click="removeEdge(selectedEdge.id)">删除连线</button>
      </div>

      <div v-else class="inspector-section">
        <div class="inspector-section__title">流程摘要</div>
        <div class="summary-grid">
          <div class="summary-card">
            <strong>ID</strong>
            <span>{{ draft.id || "未保存" }}</span>
          </div>
          <div class="summary-card">
            <strong>版本</strong>
            <span>{{ draft.version || 1 }}</span>
          </div>
          <div class="summary-card">
            <strong>状态</strong>
            <span>{{ workflowStatusText(draft.status) }}</span>
          </div>
          <div class="summary-card">
            <strong>图配置大小</strong>
            <span>{{ serializedGraph.length }} 字符</span>
          </div>
        </div>
        <label class="form-field">
          <span>流程 JSON 预览</span>
          <textarea :value="serializedGraphPretty" rows="14" readonly spellcheck="false" />
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createAgentWorkflow,
  deleteAgentWorkflow,
  fetchAgentWorkflowDetail,
  fetchAgentWorkflowList,
  fetchAgentWorkflowNodeCatalog,
  updateAgentWorkflow
} from "../api/agentWorkflow"
import {
  WORKFLOW_PROMPT_DEFAULTS,
  WORKFLOW_PROMPT_TOKEN_GROUPS
} from "../constants/agentWorkflowPrompts"

const NODE_WIDTH = 210
const NODE_HEIGHT = 168

export default {
  name: "AgentWorkflowView",
  data() {
    return {
      loading: false,
      saving: false,
      workflows: [],
      nodeCatalog: [],
      draft: this.buildEmptyWorkflow(),
      selectedNodeId: null,
      selectedEdgeId: null,
      linkSourceNodeId: null,
      nodeConfigDraft: "{}",
      dragState: null,
      canvasPanState: null,
      activeWorkspaceTab: "prompts",
      activePromptPanel: "main-system"
    }
  },
  computed: {
    selectedNode() {
      return this.draft.graph.nodes.find(item => item.id === this.selectedNodeId) || null
    },
    selectedEdge() {
      return this.draft.graph.edges.find(item => item.id === this.selectedEdgeId) || null
    },
    serializedGraph() {
      return JSON.stringify(this.draft.graph)
    },
    serializedGraphPretty() {
      return JSON.stringify(this.draft.graph, null, 2)
    },
    linkSourceNodeLabel() {
      const node = this.draft.graph.nodes.find(item => item.id === this.linkSourceNodeId)
      return node ? node.label : ""
    },
    edgeLines() {
      const nodeMap = this.draft.graph.nodes.reduce((result, item) => {
        result[item.id] = item
        return result
      }, {})
      return this.draft.graph.edges
        .map(edge => {
          const source = nodeMap[edge.source]
          const target = nodeMap[edge.target]
          if (!source || !target) {
            return null
          }
          const x1 = source.x + NODE_WIDTH / 2
          const y1 = source.y + NODE_HEIGHT / 2
          const x2 = target.x + NODE_WIDTH / 2
          const y2 = target.y + NODE_HEIGHT / 2
          return {
            ...edge,
            x1,
            y1,
            x2,
            y2,
            labelX: (x1 + x2) / 2,
            labelY: (y1 + y2) / 2 - 8
          }
        })
        .filter(Boolean)
    }
  },
  created() {
    this.initialize()
  },
  beforeDestroy() {
    this.detachDragListeners()
    this.detachCanvasPanListeners()
  },
  methods: {
    async initialize() {
      this.loading = true
      try {
        const [workflowRes, nodeCatalogRes] = await Promise.all([
          fetchAgentWorkflowList(),
          fetchAgentWorkflowNodeCatalog()
        ])
        this.workflows = workflowRes.data || []
        this.nodeCatalog = nodeCatalogRes.data || []
        if (this.workflows.length > 0) {
          await this.openWorkflow(this.workflows[0].id)
        }
      } finally {
        this.loading = false
      }
    },
    buildEmptyWorkflow() {
      return {
        id: null,
        code: "",
        name: "新建 Agent 流程",
        description: "",
        status: "DRAFT",
        version: 1,
        graph: {
          nodes: [
            {
              id: "node-start",
              type: "START",
              label: "开始",
              description: "流程入口节点。",
              x: 80,
              y: 140,
              config: { role: "entry" }
            },
            {
              id: "node-end",
              type: "END",
              label: "结束",
              description: "流程结束节点。",
              x: 420,
              y: 140,
              config: { result: "COMPLETED" }
            }
          ],
          edges: [
            {
              id: "edge-start-end",
              source: "node-start",
              target: "node-end",
              label: ""
            }
          ]
        }
      }
    },
    normalizeGraph(graph) {
      const safeGraph = graph && typeof graph === "object" ? graph : {}
      return {
        nodes: Array.isArray(safeGraph.nodes)
          ? safeGraph.nodes.map(node => ({
              id: node.id || this.uid("node"),
              type: node.type || "NOTE",
              label: node.label || node.type || "节点",
              description: node.description || "",
              x: Number.isFinite(node.x) ? node.x : 120,
              y: Number.isFinite(node.y) ? node.y : 120,
              config: node.config && typeof node.config === "object" ? node.config : {}
            }))
          : [],
        edges: Array.isArray(safeGraph.edges)
          ? safeGraph.edges
              .filter(edge => edge && edge.source && edge.target)
              .map(edge => ({
                id: edge.id || this.uid("edge"),
                source: edge.source,
                target: edge.target,
                label: edge.label || ""
              }))
          : []
      }
    },
    async openWorkflow(id) {
      const res = await fetchAgentWorkflowDetail(id)
      const item = res.data || {}
      let graph = this.buildEmptyWorkflow().graph
      try {
        graph = this.normalizeGraph(JSON.parse(item.graphJson || "{}"))
      } catch (error) {
        graph = this.buildEmptyWorkflow().graph
      }
      this.draft = {
        id: item.id,
        code: item.code || "",
        name: item.name || "",
        description: item.description || "",
        status: item.status || "DRAFT",
        version: item.version || 1,
        graph
      }
      this.clearSelection()
    },
    createDraftWorkflow() {
      this.draft = this.buildEmptyWorkflow()
      this.clearSelection()
    },
    clearSelection() {
      this.selectedNodeId = null
      this.selectedEdgeId = null
      this.linkSourceNodeId = null
      this.nodeConfigDraft = "{}"
    },
    addNode(item) {
      const offset = this.draft.graph.nodes.length * 24
      const config = this.parseJsonSafe(item.configHint, {})
      const node = {
        id: this.uid("node"),
        type: item.type,
        label: item.label,
        description: item.description,
        x: 120 + offset,
        y: 120 + offset,
        config
      }
      this.draft.graph.nodes.push(node)
      this.selectNode(node.id)
    },
    duplicateNode(node) {
      const clone = {
        ...node,
        id: this.uid("node"),
        label: `${node.label}（副本）`,
        x: node.x + 36,
        y: node.y + 36,
        config: JSON.parse(JSON.stringify(node.config || {}))
      }
      this.draft.graph.nodes.push(clone)
      this.selectNode(clone.id)
    },
    canLinkToNode(nodeId) {
      return Boolean(this.linkSourceNodeId && this.linkSourceNodeId !== nodeId)
    },
    toggleLinkMode(nodeId) {
      if (this.linkSourceNodeId === nodeId) {
        this.cancelLinkMode()
        return
      }
      this.linkSourceNodeId = nodeId
      this.selectedNodeId = nodeId
      this.selectedEdgeId = null
    },
    cancelLinkMode() {
      this.linkSourceNodeId = null
    },
    cancelLinkModeIfBlank(event) {
      if (this.isBlankCanvasTarget(event.target)) {
        this.cancelLinkMode()
      }
    },
    isBlankCanvasTarget(target) {
      if (!(target instanceof Element)) {
        return false
      }
      if (target.closest(".flow-node, .edge-line, .edge-label")) {
        return false
      }
      return Boolean(target.closest(".canvas-shell, .canvas-area, .canvas-svg"))
    },
    connectToNode(targetNodeId) {
      if (!this.linkSourceNodeId || this.linkSourceNodeId === targetNodeId) {
        return
      }
      this.addEdge(this.linkSourceNodeId, targetNodeId)
      this.linkSourceNodeId = null
      this.selectNode(targetNodeId)
    },
    selectNode(nodeId) {
      this.selectedNodeId = nodeId
      this.selectedEdgeId = null
      const node = this.draft.graph.nodes.find(item => item.id === nodeId)
      this.nodeConfigDraft = JSON.stringify((node && node.config) || {}, null, 2)
    },
    selectEdge(edgeId) {
      this.selectedEdgeId = edgeId
      this.selectedNodeId = null
      this.linkSourceNodeId = null
    },
    addEdge(sourceId, targetId) {
      if (sourceId === targetId) {
        return
      }
      const exists = this.draft.graph.edges.some(item => item.source === sourceId && item.target === targetId)
      if (exists) {
        window.alert("这两个节点之间已经有连线了。")
        return
      }
      const edge = {
        id: this.uid("edge"),
        source: sourceId,
        target: targetId,
        label: ""
      }
      this.draft.graph.edges.push(edge)
      this.selectEdge(edge.id)
    },
    edgeNodeLabel(nodeId) {
      const node = this.draft.graph.nodes.find(item => item.id === nodeId)
      return node ? node.label : nodeId
    },
    isModelNode(node) {
      return Boolean(node && node.type === "MODEL")
    },
    modelPromptVariableTokens(node) {
      if (!node) {
        return []
      }
      if (node.id === "reply-render") {
        return [
          "{{message}}",
          "{{status}}",
          "{{action}}",
          "{{toolName}}",
          "{{mutationCompleted}}",
          "{{toolOutput}}",
          "{{petName}}",
          "{{species}}",
          "{{sex}}",
          "{{currentWeight}}",
          "{{recordType}}",
          "{{recordId}}",
          "{{petId}}",
          "{{errorMessage}}"
        ]
      }
      return [
        "{{message}}",
        "{{pets}}",
        "{{memory}}",
        "{{recentHistory}}",
        "{{draft}}",
        "{{lastAiMessage}}",
        "{{lastUserMessage}}"
      ]
    },
    workflowNodeById(nodeId) {
      return this.draft.graph.nodes.find(item => item.id === nodeId) || null
    },
    workflowPromptVariableTokens(nodeId) {
      return this.modelPromptVariableTokens({ id: nodeId, type: "MODEL" })
    },
    workflowPromptTokenDetails(nodeId) {
      return nodeId === "reply-render" ? WORKFLOW_PROMPT_TOKEN_GROUPS.reply : WORKFLOW_PROMPT_TOKEN_GROUPS.main
    },
    workflowPromptDefaultValue(nodeId, field) {
      const nodeDefaults = WORKFLOW_PROMPT_DEFAULTS[nodeId]
      if (!nodeDefaults || typeof nodeDefaults[field] === "undefined" || nodeDefaults[field] === null) {
        return ""
      }
      return nodeDefaults[field]
    },
    workflowPromptFieldValue(nodeId, field) {
      const node = this.workflowNodeById(nodeId)
      if (!node || !node.config || typeof node.config !== "object") {
        return ""
      }
      return typeof node.config[field] === "undefined" || node.config[field] === null ? "" : node.config[field]
    },
    restoreWorkflowNodePromptField(nodeId, field) {
      const defaultValue = this.workflowPromptDefaultValue(nodeId, field)
      if (field === "maxTokens") {
        this.updateWorkflowNodePromptNumberField(nodeId, field, defaultValue)
        return
      }
      this.updateWorkflowNodePromptField(nodeId, field, defaultValue)
    },
    restoreWorkflowPromptGroup(nodeId) {
      const nodeDefaults = WORKFLOW_PROMPT_DEFAULTS[nodeId] || {}
      Object.keys(nodeDefaults).forEach(field => {
        this.restoreWorkflowNodePromptField(nodeId, field)
      })
    },
    updateWorkflowNodePromptField(nodeId, field, value) {
      const node = this.workflowNodeById(nodeId)
      if (!node) {
        return
      }
      if (!node.config || typeof node.config !== "object") {
        node.config = {}
      }
      node.config[field] = value
      if (this.selectedNodeId === nodeId) {
        this.syncSelectedNodeConfigDraft()
      }
    },
    appendWorkflowNodePromptToken(nodeId, field, token) {
      const node = this.workflowNodeById(nodeId)
      if (!node) {
        return
      }
      if (!node.config || typeof node.config !== "object") {
        node.config = {}
      }
      const currentValue = node.config[field] || ""
      node.config[field] = currentValue ? `${currentValue}${token}` : token
      if (this.selectedNodeId === nodeId) {
        this.syncSelectedNodeConfigDraft()
      }
    },
    updateWorkflowNodePromptNumberField(nodeId, field, value) {
      const node = this.workflowNodeById(nodeId)
      if (!node) {
        return
      }
      if (!node.config || typeof node.config !== "object") {
        node.config = {}
      }
      if (value === "" || value === null || typeof value === "undefined") {
        delete node.config[field]
      } else {
        node.config[field] = Number(value)
      }
      if (this.selectedNodeId === nodeId) {
        this.syncSelectedNodeConfigDraft()
      }
    },
    syncSelectedNodeConfigDraft() {
      if (!this.selectedNode) {
        this.nodeConfigDraft = "{}"
        return
      }
      this.nodeConfigDraft = JSON.stringify(this.selectedNode.config || {}, null, 2)
    },
    updateSelectedNodeConfigField(field, value) {
      if (!this.selectedNode) {
        return
      }
      if (!this.selectedNode.config || typeof this.selectedNode.config !== "object") {
        this.selectedNode.config = {}
      }
      this.selectedNode.config[field] = value
      this.syncSelectedNodeConfigDraft()
    },
    appendPromptToken(field, token) {
      if (!this.selectedNode) {
        return
      }
      if (!this.selectedNode.config || typeof this.selectedNode.config !== "object") {
        this.selectedNode.config = {}
      }
      const currentValue = this.selectedNode.config[field] || ""
      this.selectedNode.config[field] = currentValue ? `${currentValue}${token}` : token
      this.syncSelectedNodeConfigDraft()
    },
    updateSelectedNodeConfigNumberField(field, value) {
      if (!this.selectedNode) {
        return
      }
      if (!this.selectedNode.config || typeof this.selectedNode.config !== "object") {
        this.selectedNode.config = {}
      }
      if (value === "" || value === null || typeof value === "undefined") {
        delete this.selectedNode.config[field]
      } else {
        this.selectedNode.config[field] = Number(value)
      }
      this.syncSelectedNodeConfigDraft()
    },
    removeNode(nodeId) {
      this.draft.graph.nodes = this.draft.graph.nodes.filter(item => item.id !== nodeId)
      this.draft.graph.edges = this.draft.graph.edges.filter(
        item => item.source !== nodeId && item.target !== nodeId
      )
      if (this.selectedNodeId === nodeId || this.linkSourceNodeId === nodeId) {
        this.clearSelection()
      }
    },
    removeEdge(edgeId) {
      this.draft.graph.edges = this.draft.graph.edges.filter(item => item.id !== edgeId)
      if (this.selectedEdgeId === edgeId) {
        this.selectedEdgeId = null
      }
    },
    applyNodeConfig() {
      if (!this.selectedNode) {
        return
      }
      try {
        this.selectedNode.config = JSON.parse(this.nodeConfigDraft || "{}")
        this.syncSelectedNodeConfigDraft()
      } catch (error) {
        window.alert("节点配置 JSON 格式不正确。")
        this.nodeConfigDraft = JSON.stringify(this.selectedNode.config || {}, null, 2)
      }
    },
    startNodeDrag(node, event) {
      event.preventDefault()
      this.selectNode(node.id)
      this.dragState = {
        nodeId: node.id,
        startX: event.clientX,
        startY: event.clientY,
        originX: node.x,
        originY: node.y
      }
      window.addEventListener("mousemove", this.handleDragMove)
      window.addEventListener("mouseup", this.handleDragEnd)
    },
    handleDragMove(event) {
      if (!this.dragState) {
        return
      }
      const node = this.draft.graph.nodes.find(item => item.id === this.dragState.nodeId)
      if (!node) {
        return
      }
      node.x = Math.max(16, this.dragState.originX + event.clientX - this.dragState.startX)
      node.y = Math.max(16, this.dragState.originY + event.clientY - this.dragState.startY)
    },
    handleDragEnd() {
      this.detachDragListeners()
      this.dragState = null
    },
    detachDragListeners() {
      window.removeEventListener("mousemove", this.handleDragMove)
      window.removeEventListener("mouseup", this.handleDragEnd)
    },
    startCanvasPan(event) {
      if (event.button !== 0 || this.dragState || !this.isBlankCanvasTarget(event.target)) {
        return
      }
      const shell = this.$refs.canvasShell
      if (!shell) {
        return
      }
      event.preventDefault()
      this.canvasPanState = {
        startX: event.clientX,
        startY: event.clientY,
        scrollLeft: shell.scrollLeft,
        scrollTop: shell.scrollTop
      }
      window.addEventListener("mousemove", this.handleCanvasPanMove)
      window.addEventListener("mouseup", this.handleCanvasPanEnd)
    },
    handleCanvasPanMove(event) {
      if (!this.canvasPanState) {
        return
      }
      const shell = this.$refs.canvasShell
      if (!shell) {
        return
      }
      event.preventDefault()
      shell.scrollLeft = this.canvasPanState.scrollLeft - (event.clientX - this.canvasPanState.startX)
      shell.scrollTop = this.canvasPanState.scrollTop - (event.clientY - this.canvasPanState.startY)
    },
    handleCanvasPanEnd() {
      this.detachCanvasPanListeners()
      this.canvasPanState = null
    },
    detachCanvasPanListeners() {
      window.removeEventListener("mousemove", this.handleCanvasPanMove)
      window.removeEventListener("mouseup", this.handleCanvasPanEnd)
    },
    async saveWorkflow() {
      this.applyNodeConfig()
      this.saving = true
      try {
        const payload = {
          name: this.draft.name,
          code: this.draft.code,
          description: this.draft.description,
          status: this.draft.status,
          graphJson: this.serializedGraph
        }
        const res = this.draft.id
          ? await updateAgentWorkflow(this.draft.id, payload)
          : await createAgentWorkflow(payload)
        const saved = res.data || {}
        await this.reloadWorkflowList(saved.id)
        window.alert("流程已保存。")
      } finally {
        this.saving = false
      }
    },
    async reloadWorkflowList(preferredId) {
      const res = await fetchAgentWorkflowList()
      this.workflows = res.data || []
      if (preferredId) {
        await this.openWorkflow(preferredId)
      }
    },
    async removeCurrentWorkflow() {
      if (!this.draft.id) {
        this.createDraftWorkflow()
        return
      }
      if (!window.confirm("确认删除这个流程吗？")) {
        return
      }
      await deleteAgentWorkflow(this.draft.id)
      await this.reloadWorkflowList()
      if (this.workflows.length > 0) {
        await this.openWorkflow(this.workflows[0].id)
      } else {
        this.createDraftWorkflow()
      }
    },
    parseJsonSafe(text, fallback) {
      try {
        return text ? JSON.parse(text) : fallback
      } catch (error) {
        return fallback
      }
    },
    workflowStatusText(status) {
      if (status === "ACTIVE") {
        return "启用"
      }
      if (status === "DISABLED") {
        return "停用"
      }
      return "草稿"
    },
    catalogCategoryText(category) {
      if (category === "control") {
        return "控制"
      }
      if (category === "reasoning") {
        return "推理"
      }
      if (category === "tool") {
        return "工具"
      }
      if (category === "utility") {
        return "辅助"
      }
      return category || ""
    },
    uid(prefix) {
      return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`
    }
  }
}
</script>

<style scoped>
.workflow-page {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr) 320px;
  gap: 16px;
  align-items: start;
}

.workflow-sidebar,
.workflow-inspector {
  position: sticky;
  top: 0;
}

.workflow-sidebar__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.workflow-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 18px;
}

.workflow-item {
  width: 100%;
  padding: 14px;
  border: 1px solid #e8edf3;
  border-radius: 16px;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
}

.workflow-item--active {
  border-color: #2563eb;
  background: #eff6ff;
}

.workflow-item__title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.workflow-item__meta {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
}

.workflow-item__desc {
  margin-top: 8px;
  color: #475569;
  font-size: 13px;
  line-height: 1.5;
}

.sidebar-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #eef2f7;
}

.sidebar-section__title,
.inspector-section__title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.catalog-list {
  display: grid;
  gap: 10px;
  margin-top: 14px;
}

.catalog-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  background: #f8fafc;
  cursor: pointer;
}

.catalog-item__label {
  font-weight: 600;
  color: #0f172a;
}

.catalog-item__type {
  color: #64748b;
  font-size: 12px;
}

.workflow-main {
  display: grid;
  gap: 16px;
}

.workspace-switcher {
  display: grid;
  gap: 12px;
}

.workspace-switcher__tabs {
  display: inline-flex;
  width: fit-content;
  padding: 4px;
  border-radius: 16px;
  background: #f1f5f9;
  gap: 4px;
}

.workspace-switcher__tab {
  min-width: 120px;
  height: 38px;
  padding: 0 16px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: #475569;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.workspace-switcher__tab--active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
}

.workspace-switcher__hint {
  color: #64748b;
  font-size: 13px;
}

.workflow-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-end;
}

.toolbar-fields {
  flex: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) 120px;
  gap: 12px;
}

.toolbar-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.canvas-card {
  padding-bottom: 16px;
}

.canvas-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.canvas-meta {
  display: flex;
  gap: 10px;
  color: #64748b;
  font-size: 13px;
}

.link-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #fde68a;
  border-radius: 12px;
  background: #fffbeb;
  color: #92400e;
  font-size: 13px;
}

.canvas-shell {
  overflow: auto;
  border: 1px solid #e8edf3;
  border-radius: 20px;
  background:
    linear-gradient(#f8fbff 1px, transparent 1px),
    linear-gradient(90deg, #f8fbff 1px, transparent 1px),
    #ffffff;
  background-size: 24px 24px;
  cursor: grab;
}

.canvas-shell--panning {
  cursor: grabbing;
}

.canvas-area {
  position: relative;
  min-height: 720px;
  min-width: 1120px;
  cursor: inherit;
}

.canvas-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.edge-line {
  stroke: #94a3b8;
  stroke-width: 2;
  cursor: pointer;
}

.edge-line--active {
  stroke: #2563eb;
  stroke-width: 3;
}

.edge-label {
  fill: #475569;
  font-size: 12px;
  text-anchor: middle;
  cursor: pointer;
}

.flow-node {
  position: absolute;
  width: 210px;
  min-height: 168px;
  padding: 14px;
  border: 1px solid #dbe4f2;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
}

.flow-node--selected {
  border-color: #2563eb;
  box-shadow: 0 16px 32px rgba(37, 99, 235, 0.14);
}

.flow-node--linking {
  border-color: #f59e0b;
  box-shadow: 0 16px 32px rgba(245, 158, 11, 0.16);
}

.flow-node--link-target {
  border-style: dashed;
  border-color: #22c55e;
}

.flow-node__top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
}

.flow-node__type {
  padding: 4px 8px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 700;
}

.node-icon-button {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
}

.flow-node__drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  margin-top: 10px;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  cursor: move;
  user-select: none;
}

.flow-node__title {
  margin-top: 12px;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.flow-node__desc {
  margin-top: 8px;
  color: #475569;
  font-size: 13px;
  line-height: 1.5;
}

.flow-node__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.mini-button {
  height: 30px;
  padding: 0 12px;
  border: 1px solid #dbe4f2;
  border-radius: 999px;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
  font-size: 12px;
}

.mini-button--primary {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}

.mini-button--warn {
  border-color: #f59e0b;
  background: #fff7ed;
  color: #b45309;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-field span {
  color: #475569;
  font-size: 13px;
  font-weight: 600;
}

.form-field input,
.form-field textarea,
.form-field select {
  width: 100%;
  padding: 11px 12px;
  border: 1px solid #dbe4f2;
  border-radius: 12px;
  color: #0f172a;
  background: #ffffff;
  font-size: 14px;
  resize: vertical;
}

.form-field--small {
  max-width: 120px;
}

.position-row {
  display: flex;
  gap: 12px;
}

.prompt-editor {
  display: grid;
  gap: 12px;
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #dbe4f2;
  border-radius: 14px;
  background: #f8fafc;
}

.prompt-editor__title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.prompt-editor__hint {
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
  word-break: break-word;
}

.prompt-editor__vars {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.token-button {
  height: 28px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #ffffff;
  color: #334155;
  font-size: 12px;
  cursor: pointer;
}

.workflow-prompt-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.workflow-prompt-grid--single {
  grid-template-columns: minmax(0, 1fr);
}

.prompt-workbench {
  display: grid;
  gap: 18px;
}

.ghost-button--mini {
  height: 34px;
  padding: 0 12px;
  font-size: 12px;
}

.prompt-workbench__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 18px;
}

.prompt-switcher {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.prompt-switcher__item {
  display: grid;
  gap: 6px;
  padding: 14px;
  border: 1px solid #dbe4f2;
  border-radius: 14px;
  background: #f8fafc;
  text-align: left;
  cursor: pointer;
  min-width: 220px;
  flex: 1 1 220px;
}

.prompt-switcher__item strong {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.prompt-switcher__item span {
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
}

.prompt-switcher__item--active {
  border-color: #2563eb;
  background: #eff6ff;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.08);
}

.prompt-panel {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.prompt-panel__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.prompt-panel__meta {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
}

.prompt-editor--flat {
  margin-top: 0;
}

.prompt-field {
  display: grid;
  gap: 10px;
}

.prompt-field__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.prompt-field__label {
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  flex: 0 0 auto;
}

.prompt-field__path {
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
  word-break: break-all;
}

.prompt-field__textarea {
  width: 100%;
  min-height: 280px;
  padding: 11px 12px;
  border: 1px solid #dbe4f2;
  border-radius: 12px;
  color: #0f172a;
  background: #ffffff;
  font-size: 14px;
  resize: vertical;
}

.prompt-field__textarea--compact {
  min-height: 180px;
}

.prompt-inline-field {
  display: inline-flex;
  flex-direction: column;
  gap: 8px;
  width: 160px;
}

.prompt-inline-field span {
  color: #475569;
  font-size: 13px;
  font-weight: 600;
}

.prompt-inline-field input {
  width: 100%;
  padding: 11px 12px;
  border: 1px solid #dbe4f2;
  border-radius: 12px;
  color: #0f172a;
  background: #ffffff;
  font-size: 14px;
}

.prompt-sidecard {
  padding: 14px;
  border: 1px solid #dbe4f2;
  border-radius: 14px;
  background: #f8fafc;
}

.prompt-sidecard--sticky {
  position: sticky;
  top: 24px;
  align-self: start;
}

.prompt-sidecard__title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.prompt-sidecard__desc {
  margin-top: 6px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
}

.prompt-token-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.prompt-token-item {
  display: grid;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}

.prompt-token-item code {
  color: #1d4ed8;
  font-size: 12px;
}

.prompt-token-item span {
  color: #475569;
  font-size: 12px;
  line-height: 1.6;
}

.inspector-section {
  margin-top: 18px;
}

.edge-summary {
  display: grid;
  gap: 8px;
  margin: 12px 0 16px;
  padding: 12px;
  border-radius: 12px;
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 16px 0;
}

.summary-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e8edf3;
}

.summary-card strong {
  font-size: 12px;
  color: #64748b;
}

.summary-card span {
  font-size: 14px;
  color: #0f172a;
  word-break: break-all;
}

@media (max-width: 1440px) {
  .workflow-page {
    grid-template-columns: 260px minmax(0, 1fr);
  }

  .workflow-inspector {
    grid-column: 1 / -1;
    position: static;
  }
}

@media (max-width: 1080px) {
  .workflow-page {
    grid-template-columns: 1fr;
  }

  .workflow-sidebar {
    position: static;
  }

  .workflow-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-fields {
    grid-template-columns: 1fr;
  }

  .toolbar-actions {
    justify-content: flex-end;
  }

  .prompt-workbench__layout {
    grid-template-columns: 1fr;
  }

  .prompt-switcher {
    flex-direction: column;
  }

  .workflow-prompt-grid {
    grid-template-columns: 1fr;
  }

  .prompt-sidecard--sticky {
    position: static;
  }
}

@media (min-width: 1441px) {
  .workflow-page--prompt-mode {
    grid-template-columns: 300px minmax(0, 1fr);
  }
}
</style>
